/**
 * Data provider interface and shared product types.
 *
 * All concrete data providers (REST, mock, etc.) must satisfy IDataProvider so
 * the widget remains backend-agnostic.
 *
 * Product types are generic. 3-D model-oriented widgets can use Product or
 * extend it, while 2-D overlays can supply their own product shape.
 */

/** Per-mesh material override definition. */
export interface MeshMaterialOverride {
  color?: string;
  bumpScale?: number;
  aoMapIntensity?: number;
  depthWrite?: boolean;
  textures?: Record<string, string>;
}

/** A colour/finish variant of a product. */
export interface Variant {
  /** Unique identifier for this variant. */
  id: string;
  /** Human-readable variant name (e.g. "Matte Black", "Rose Gold"). */
  name: string;
  /** Optional hex colour swatch (e.g. "#2d2d2d"). */
  color?: string;
  /** Optional URL to a texture override for this variant. */
  textureUrl?: string;
  /** Optional per-mesh material overrides for this variant. */
  materialOverrides?: Record<string, MeshMaterialOverride>;
  /** Optional URL to a variant thumbnail image. */
  thumbnailUrl?: string;
}

// ---------------------------------------------------------------------------
// Eyewear catalog taxonomy constants
// ---------------------------------------------------------------------------

/** Valid audience tags for eyewear products. */
export const AUDIENCES = ['men', 'women', 'unisex', 'kids'] as const;
/** Union type for audience tags. */
export type Audience = (typeof AUDIENCES)[number];

/** Valid frame shape values for eyewear products. */
export const FRAME_SHAPES = [
  'round',
  'square',
  'cat-eye',
  'aviator',
  'rectangle',
  'oval',
  'wayfarer',
  'browline',
  'rimless',
] as const;
/** Union type for frame shapes. */
export type FrameShape = (typeof FRAME_SHAPES)[number];

/** A single product -- base type for all widget domains. */
export interface Product {
  /** Unique product identifier. */
  id: string;
  /** Display name. */
  name: string;
  /** URL of the GLTF/GLB 3-D model. */
  modelUrl: string;
  /** URL of the product thumbnail image (used in the catalog strip). */
  thumbnailUrl: string;
  /** Available colour/finish variants. */
  variants: Variant[];
  /**
   * Optional position offset applied after model auto-centering.
   * Tuple of [x, y, z] in scene units. Additive to auto-centered position.
   */
  positionOffset?: [number, number, number];
  /**
   * Optional scale multiplier applied after model auto-normalization.
   * Multiplicative on the auto-normalized scale.
   */
  scale?: number;

  // ---------------------------------------------------------------------------
  // Eyewear-specific optional fields (Sprint 2 — Nop-Tech VTO integration)
  // ---------------------------------------------------------------------------

  /** Target audience tag. */
  audience?: Audience;
  /** Free-form style tag (e.g. "classic", "bold", "minimal", "sport"). */
  style?: string;
  /** Frame shape (eyewear-specific). */
  shape?: FrameShape;
  /** Available frame sizes (e.g. ["S", "M", "L"] or ["48mm", "50mm", "52mm"]). */
  sizes?: string[];
  /**
   * Physical full frame width in millimetres, temple-to-temple/front width.
   * Used for PD-relative fit calibration when merchant/catalog dimensions are available.
   */
  frameWidthMm?: number;
  /** Optional lens width in millimetres, usually printed on eyewear arms. */
  lensWidthMm?: number;
  /** Optional bridge width in millimetres, usually printed on eyewear arms. */
  bridgeWidthMm?: number;
  /** Optional temple length in millimetres, usually printed on eyewear arms. */
  templeLengthMm?: number;
  /** Reference PD used when deriving frame width/IPD ratios from physical dimensions. */
  referencePdMm?: number;
  /** Brand (used by similar-frames algorithm later). */
  brand?: string;
  /** Price in store currency, minor units (e.g. 13900 = €139.00). */
  priceMinor?: number;
  /** ISO 4217 currency code (e.g. "EUR"). */
  currency?: string;
}

/**
 * Contract every data provider must implement.
 *
 * The generic type parameter P allows widget-specific product types to be
 * used while maintaining a consistent interface shape. Some verticals, such
 * as makeup, use 2-D products without GLB model fields.
 *
 * Methods are asynchronous to accommodate remote API calls, caching layers,
 * or mock data generation.
 */
export interface IDataProvider<P = Product> {
  /**
   * Fetch the full product catalog for a given store.
   *
   * @param storeId - Store/tenant identifier.
   * @returns Array of products available in this store.
   */
  getCatalog(storeId: string): Promise<P[]>;

  /**
   * Fetch a single product by ID.
   *
   * @param productId - Product identifier.
   * @returns The requested product.
   * @throws {DataProviderError} if the product is not found.
   */
  getProduct(productId: string): Promise<P>;

  /**
   * Resolve the 3-D model URL for a given product.
   *
   * Convenience method -- some backends serve a signed/CDN URL that differs
   * from the stored modelUrl and may expire.
   *
   * @param productId - Product identifier.
   * @returns Resolved URL string.
   */
  getModelUrl(productId: string): Promise<string>;
}

/** Error thrown by data provider operations. */
export class DataProviderError extends Error {
  public readonly statusCode: number | undefined
  public readonly cause?: unknown

  constructor(
    message: string,
    statusCode?: number,
    cause?: unknown,
  ) {
    super(message);
    this.statusCode = statusCode;
    this.cause = cause;
    this.name = 'DataProviderError';
  }
}
